/**
 * Deck slice — normalized cards map, deck properties, and all mutations.
 *
 * Cards are stored as `Record<deckName, Record<cardId, CardProps>>` for:
 * - O(1) per-card mutations (vs O(n) array map+diff)
 * - Per-card Zustand reactivity (only the changed card triggers re-renders)
 * - ~1 KB IndexedDB writes per card edit (vs ~15 MB full deck blob)
 */

import { stampChangedCards } from '@flashka/shared/sync-engine/framework/sync-merge';
import type { SyncCardProps as CardProps } from '@flashka/shared/sync-engine/schema/shared-types';
import { isCardDeleted } from '@flashka/shared/sync-engine/schema/shared-types';

import type { StateCreator } from 'zustand';

import { getSyncClientCapabilities } from '../../../sync-engine/framework/client-capabilities';
import { DECK_STUDY_SUMMARY_TABLE } from '../../../sync-engine/framework/local-tables';
import type { DeckProperties, StoredDeckSummary } from '../../provider/sync-storage';
import { deleteDeckNotes } from '../../scoped-deck';
import { bucketRowCount, deckBucketComplete } from '../deck-residency';
import { drainPendingBulk } from '../entities/bulk-coalesce';
import { getMutationSource } from '../framework/mutation-source';
import { getUnsyncedDeckTracker } from '../sync-bookkeeping/unsynced-decks';
import { backfillSortKeys } from '../utils/sort-key';
import { hasMalformedStringField, stripMalformedStringFields } from './card-sanitize';
import type { FlashkaState } from './flashka-store';
import { getSyncStorage, mutate, safeBulkPut, safeBulkRemove } from './store-utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Known-bad fields that leak into card objects via UI/routing spreads
 * and must NOT reach Zustand, Dexie, or the wire. The historical
 * offender is `deckName`, which the SG folder hooks tag onto cards for
 * routing in `use-load-sg-folder-decks.ts:355` — `getUpdatedCardAction`
 * spreads it through into transform inputs and study writes.
 *
 * NOTE: this is a DENYLIST, not an allowlist. An older client must
 * forward-propagate fields it doesn't yet know about (e.g. a `label`
 * field added in a newer schema version on Device 2) so that a
 * round-trip Device-2-write → Device-1-sync → Device-1-edit →
 * Device-2-merge does not silently drop the new content. The only
 * downstream consumer that demanded strict cleaning was
 * `partitionByScope`, which now uses `onUnknown: "ignore"` at every
 * call site (see `linked-deck-propagation.ts:75-77`). See
 * SYNC_ENGINE_BUGS.md "Schema drift" for the full round-trip
 * regression.
 */
const STRIPPED_CARD_KEYS: ReadonlySet<string> = new Set(['deckName']);

/**
 * Strip known-bad pollution fields from a card AND backfill the
 * personal-scope arrays that direct readers expect to be present.
 *
 * Cheap and idempotent — called at every store write boundary
 * (`setCard`, `setCardsBatch`, `updateDeckCards`, `setDeckFromSync`)
 * so polluted cards never make it into Zustand or Dexie. Unknown
 * fields are preserved for forward-compatibility with newer schema
 * versions.
 *
 * Backfill rationale: the SG-side projection strips personal-scope
 * fields (`actions`, `studyState`) per CARD_FIELD_SCOPES. When
 * `saveToDexie` writes an SG card into the personal store (either
 * brand-new mirror seed or `mergeCardsLWW` picking the SG row on
 * tied timestamps), those arrays are absent. Direct readers in
 * `regenerate-button.tsx`, `study-utils.ts`, `memo/study-session.tsx`
 * read `card.actions.length` / `card.studyState.length` without going
 * through the composed-deck overlay, so undefined throws and tears
 * the page down. Layering empty-array defaults at the write boundary
 * matches `scopeDefaultsForCard` in `composed-deck.ts` and keeps the
 * personal store's invariant ("every card has its personal arrays")
 * consistent across cold-hydrate, sync-init, and SG-mirror seed.
 */
function cleanCardForStore(card: CardProps): CardProps {
  // Own-enumerable membership — exactly what scanning `Object.keys(card)`
  // tested, without materialising the key array for every card of a
  // deck-wide write.
  let needsStrip = false;
  for (const key of STRIPPED_CARD_KEYS) {
    if (Object.prototype.propertyIsEnumerable.call(card, key)) {
      needsStrip = true;
      break;
    }
  }
  // Anything but an array — `undefined` from an old row, but also `null` from
  // a server diff or a mirrored row — is repaired to empty: every reader does
  // `.length` on these, and one launch-time throw repeats on every relaunch.
  const needsActionsBackfill = !Array.isArray(card.actions);
  const needsStudyStateBackfill = !Array.isArray(card.studyState);
  const needsStringCoerce = hasMalformedStringField(card as Record<string, unknown>);
  if (
    !needsStrip &&
    !needsActionsBackfill &&
    !needsStudyStateBackfill &&
    !needsStringCoerce
  )
    return card;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(card)) {
    if (!STRIPPED_CARD_KEYS.has(key)) {
      out[key] = (card as Record<string, unknown>)[key];
    }
  }
  if (needsActionsBackfill) out.actions = [];
  if (needsStudyStateBackfill) out.studyState = [];
  if (needsStringCoerce) stripMalformedStringFields(out);
  return out as CardProps;
}

/**
 * Remember a LOCAL card write for the reconnect flush. `flushLocal` walks
 * resident buckets only, so a deck edited while the socket was not ready must
 * be resident at the next connect — the tracker is what hydrate force-loads.
 * Server-sourced writes are already the server's truth and are skipped.
 */
function noteLocalCardWrite(deckId: string): void {
  if (getMutationSource() === 'server') return;
  getUnsyncedDeckTracker().mark(deckId);
}

/** Convert a card array to a Record keyed by card id. */
function cardsArrayToMap(cards: CardProps[]): Record<string, CardProps> {
  const map: Record<string, CardProps> = {};
  for (const c of cards) map[c.id] = c;
  return map;
}

/** Convert a card map to an array (for sync engine / legacy compatibility). */
export function cardsMapToArray(map: Record<string, CardProps> | undefined): CardProps[] {
  return map ? Object.values(map) : [];
}

/** The summaries map without `deckId` — same object when it held no row, so an
 *  unrelated delete does not change the map's identity. */
function withoutSummary(
  summaries: Record<string, StoredDeckSummary>,
  deckId: string,
): Record<string, StoredDeckSummary> {
  if (!Object.prototype.hasOwnProperty.call(summaries, deckId)) return summaries;
  const { [deckId]: _, ...rest } = summaries;
  return rest;
}

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface DeckSlice {
  /** Normalized cards: [deckId][cardId] → CardProps. */
  cards: Record<string, Record<string, CardProps>>;
  deckProperties: Record<string, DeckProperties>;

  // ── Cold-deck cache state (LOCAL-only; never synced) ──────────────────
  /** Per-deck last-activity ISO from the server (`deckId -> ISO`). Drives the
   *  warm/cold split. Empty until the first cold-deck-aware sync-diff. */
  deckActivity: Record<string, string>;
  /** Decks whose card bodies are currently resident (warm or just-unarchived).
   *  Distinguishes "loaded, empty" from "not loaded yet" so the deck view
   *  doesn't spin forever on a genuinely-empty deck. */
  loadedDeckIds: Set<string>;
  /** Decks shown as archived (cold) — present in `deckProperties` but with
   *  card bodies withheld. Recomputed at hydrate + each cold-deck sync-diff. */
  archivedDeckIds: Set<string>;
  /**
   * Decks whose bucket holds EVERY row this device has on disk — "memory ⊇
   * disk", as opposed to `loadedDeckIds`' "memory ⊇ the deck". Eager hydration
   * marks every warm deck (their rows were all read); under lazy hydration a
   * deck joins when its rows are read (`internal/deck-disk-load.ts`) or written
   * whole. Session-only, never shrinks (no eviction). NOT a merge licence:
   * a short disk read holds fewer rows than the deck, and `deckCardsMergeSafe`
   * needs the full set.
   */
  diskLoadedDeckIds: Set<string>;
  /** Bulk-set the cold-deck view state (hydrate + sync-diff). Each provided
   *  field replaces wholesale; Sets are stored by reference so callers MUST
   *  pass fresh Sets. */
  setColdDeckState: (next: {
    deckActivity?: Record<string, string>;
    loadedDeckIds?: Set<string>;
    archivedDeckIds?: Set<string>;
  }) => void;
  /** Mark a deck unarchived (its cards just arrived / it was opened): add to
   *  loaded, drop from archived. Always allocates new Sets for re-render. */
  markDeckUnarchived: (deckId: string) => void;
  /**
   * A loader finished delivering the deck's FULL row set into the bucket:
   * loaded, not archived — and, when `coversDisk`, disk-loaded too. Unlike
   * `markDeckUnarchived` (the optimistic flip a deck open makes before its
   * cards arrive) this states a fact about the bucket, so `deckCardsResident`
   * and `deckCardsMergeSafe` may trust it. `coversDisk` is the caller's claim
   * that every row this device holds for the deck is now in memory: true after
   * a whole-deck LOCAL write (a study-group mirror merge persists every row it
   * puts in the bucket), false after a SERVER stream (`deck-cards-loaded` —
   * the archived-online path skips disk on purpose, so nothing checked it).
   */
  markDeckCardsLoaded: (deckId: string, opts: { coversDisk: boolean }) => void;
  /**
   * Merge a deck's rows read from LOCAL storage into its bucket
   * (`internal/deck-disk-load.ts`). Memory wins on a shared id — the read
   * awaited the storage flush, so a row already in memory is as new or newer.
   * The deck always joins `diskLoadedDeckIds` (every local row is now in
   * memory — also when disk had nothing), and joins `loadedDeckIds` (leaving
   * `archivedDeckIds`) only when the merged bucket holds at least `cardCount`
   * rows: a partial local set (the deck was cold when this device first
   * synced) must not read as resident, or the open flow would stop at it
   * instead of asking the server. An empty read never materialises an empty
   * bucket. No storage write (the rows came from storage), no meta stamp, no
   * card-count reconcile — the rows are already the truth on this device.
   */
  applyDeckCardsFromDisk: (deckId: string, rows: Record<string, CardProps>) => void;

  // ── Per-deck study summaries (LOCAL-only; derived, never synced) ──────
  /**
   * One `StoredDeckSummary` per deck — the counts and sorted timestamps Home
   * reads instead of the deck's card bodies (`sync-client/deck-summary`).
   * Maintained by the summary bridge (`deck-summary/store-bridge.ts`) from the
   * card buckets, loaded from disk at hydrate. A row keeps its identity until
   * ITS deck's cards change, so `pickRecord` + `useShallow` over this map is
   * shallow-stable per tile. Not a sync entity: never enters the snapshot the
   * dirty scan reads.
   */
  deckSummaries: Record<string, StoredDeckSummary>;
  /** Merge recomputed rows and drop removed ones in ONE set (one subscription
   *  fire). Owned by the summary bridge and hydrate — not for UI code. */
  applyDeckSummaries: (
    upsert: Record<string, StoredDeckSummary>,
    remove?: readonly string[],
  ) => void;

  // ── Granular O(1) card mutations ──────────────────────────────────────
  /** Set (upsert) a single card. O(1) in-memory + O(1) IndexedDB put. */
  setCard: (deckId: string, card: CardProps) => void;
  /** Set multiple cards atomically (one Zustand set, one subscription fire). */
  setCardsBatch: (deckId: string, cards: CardProps[]) => void;
  /** Remove a single card from the map and IndexedDB. */
  removeCard: (deckId: string, cardId: string) => void;
  /**
   * Remove many cards from the map and storage: ONE store set, ONE card-count
   * reconcile, ONE batched storage delete. Same end state as `removeCard` per
   * id (every id is deleted from storage even when the bucket lacks it).
   */
  removeCards: (deckId: string, cardIds: readonly string[]) => void;
  /**
   * Reconcile the persisted `cardCount` on a deck's meta to the live in-memory
   * count. An ARCHIVED deck's tile renders `deckProperties.cardCount` (its
   * cards aren't on-device), so the value must be correct as of the deck's last
   * edit — the moment before it can go cold. Called from the card add/remove
   * mutators only.
   *
   * Invariants that keep it cheap + safe:
   *   - No-op when the count is unchanged ⇒ plain card EDITS (study/star/flip,
   *     which reuse `setCard` on an existing id) never push the deck meta,
   *     preserving the "no deck-meta push per card edit" rule.
   *   - No-op when `allowed` is false — the caller's PRE-mutation
   *     `reconcileAllowed` verdict (lazy hydration: the bucket may hold only
   *     part of the deck's rows; see the helper). Eager: always allowed.
   *   - No-op for an ARCHIVED deck ⇒ its cards aren't resident, so the live
   *     count would be 0/partial and would CLOBBER the real server value.
   *   - No-op when the deck has no meta yet (still mid-creation).
   */
  reconcileDeckCardCount: (deckId: string, allowed?: boolean) => void;

  // ── Sync boundary (converts between arrays and maps) ──────────────────
  /** Apply a full deck from sync (cards come as array from server). */
  setDeckFromSync: (id: string, cards: CardProps[], updatedAtISO?: string) => void;

  // ── Transform bridge (for multi-card operations) ──────────────────────
  /**
   * Transform all cards in a deck via a function.
   * Internally: map→array→transform→diff→map. Use for multi-card ops
   * like reorder or batch flip. Prefer setCard for single-card mutations.
   */
  updateDeckCards: (id: string, transform: (cards: CardProps[]) => CardProps[]) => void;
  /** @deprecated Use setDeckFromSync instead. */
  setDeck: (id: string, cards: CardProps[], updatedAtISO?: string) => void;

  // ── Deck properties & lifecycle ───────────────────────────────────────
  /** Upsert a single deck's properties (per-id). Renamed from the prior
   *  `setDeckProperties` to avoid collision with the auto-generated
   *  indexed-collection `setDeckProperties(data: Record<id, T>)`
   *  bulk-replace setter. */
  setDeckProperty: (id: string, props: DeckProperties) => void;
  removeDeck: (id: string) => void;
  applyServerDeckDelete: (id: string) => void;
  moveDeckToFolder: (id: string, folderId: string | undefined) => void;
  reorderDeck: (id: string, sortKey: string) => void;
  /** Batch-update properties for multiple decks in one Zustand set (one subscription fire). */
  batchUpdateDeckProperties: (
    updates: Array<{ id: string; partial: Partial<DeckProperties> }>,
  ) => void;
}

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

/**
 * May a card mutator recompute `deckProperties.cardCount` from the bucket it
 * just changed? Judged on the state BEFORE the mutation.
 *
 * Eager hydration: always — every warm deck's rows are resident by
 * construction (the archived guard in `reconcileDeckCardCount` covers cold
 * decks). Lazy hydration: only when the bucket is known to be the deck's full
 * set — the deck is loaded, its bucket is complete by the cardCount yardstick,
 * or the deck is empty by every local signal (no bucket, count 0/unset: a deck
 * being created). A partial bucket (frames for a non-resident deck, a local
 * batch into a deck whose rows are on disk) must never overwrite the count
 * the tile shows: 10 rows in memory of a 500-card deck is not 10 cards.
 */
function reconcileAllowed(s: FlashkaState, deckId: string): boolean {
  if (!getSyncClientCapabilities().lazyCardHydration) return true;
  // A LOADED deck reconciles even when its bucket is short of `cardCount`: that
  // is how a stale-high count heals (`deckBucketComplete` alone would freeze
  // the count, and with it the deck's summary — the bridge writes only when
  // rows ≥ count). Safe because under the flag `loadedDeckIds` is set by the
  // loaders only, after a complete set landed — no native path flips it
  // optimistically, and `applyDeckCardsFromDisk` never marks a deck it holds
  // no rows for unless its count is an explicit 0.
  if (s.loadedDeckIds.has(deckId)) return true;
  if (deckBucketComplete(s, deckId)) return true;
  return !s.cards[deckId] && (s.deckProperties[deckId]?.cardCount ?? 0) === 0;
}

/** The residency flags after a loader delivered `deckId`'s full row set:
 *  loaded, disk-loaded, not archived. Allocates only the Sets that change. */
function residentFlags(
  s: Pick<DeckSlice, 'loadedDeckIds' | 'diskLoadedDeckIds' | 'archivedDeckIds'>,
  deckId: string,
): Partial<Pick<DeckSlice, 'loadedDeckIds' | 'diskLoadedDeckIds' | 'archivedDeckIds'>> {
  const out: Partial<
    Pick<DeckSlice, 'loadedDeckIds' | 'diskLoadedDeckIds' | 'archivedDeckIds'>
  > = {};
  if (!s.loadedDeckIds.has(deckId))
    out.loadedDeckIds = new Set(s.loadedDeckIds).add(deckId);
  if (!s.diskLoadedDeckIds.has(deckId)) {
    out.diskLoadedDeckIds = new Set(s.diskLoadedDeckIds).add(deckId);
  }
  if (s.archivedDeckIds.has(deckId)) {
    const archivedDeckIds = new Set(s.archivedDeckIds);
    archivedDeckIds.delete(deckId);
    out.archivedDeckIds = archivedDeckIds;
  }
  return out;
}

export const createDeckSlice: StateCreator<FlashkaState, [], [], DeckSlice> = (
  set,
  get,
) => ({
  cards: {},
  deckProperties: {},

  // ── Cold-deck cache state ─────────────────────────────────────────────
  deckActivity: {},
  loadedDeckIds: new Set<string>(),
  archivedDeckIds: new Set<string>(),
  diskLoadedDeckIds: new Set<string>(),

  setColdDeckState: (next) =>
    set((s) => ({
      deckActivity: next.deckActivity ?? s.deckActivity,
      loadedDeckIds: next.loadedDeckIds ?? s.loadedDeckIds,
      archivedDeckIds: next.archivedDeckIds ?? s.archivedDeckIds,
    })),

  markDeckUnarchived: (deckId) =>
    set((s) => {
      const loadedDeckIds = new Set(s.loadedDeckIds);
      loadedDeckIds.add(deckId);
      let archivedDeckIds = s.archivedDeckIds;
      if (archivedDeckIds.has(deckId)) {
        archivedDeckIds = new Set(archivedDeckIds);
        archivedDeckIds.delete(deckId);
      }
      return { loadedDeckIds, archivedDeckIds };
    }),

  markDeckCardsLoaded: (deckId, { coversDisk }) => {
    if (!coversDisk) {
      // Loaded + un-archived only: the same flags a deck open flips, now as a
      // fact. `deckCardsMergeSafe` holds for the deck (through
      // `deckBucketComplete`) once the stream delivered ≥ `cardCount` rows.
      get().markDeckUnarchived(deckId);
      return;
    }
    set((s) => residentFlags(s, deckId));
  },

  applyDeckCardsFromDisk: (deckId, rows) =>
    set((s) => {
      const existing = s.cards[deckId];
      let merged = existing;
      if (existing) {
        let added = 0;
        for (const id in rows) {
          if (!Object.prototype.hasOwnProperty.call(existing, id)) added += 1;
        }
        // Nothing new on disk: keep the bucket's identity (no downstream work).
        if (added > 0) merged = { ...rows, ...existing };
      } else if (bucketRowCount(rows) > 0) {
        merged = rows;
      }
      const cards =
        merged === existing || merged === undefined
          ? s.cards
          : { ...s.cards, [deckId]: merged };
      // Every local row is in memory now — whatever the deck's size.
      const diskLoadedDeckIds = s.diskLoadedDeckIds.has(deckId)
        ? s.diskLoadedDeckIds
        : new Set(s.diskLoadedDeckIds).add(deckId);
      // Resident only when the merged bucket is the deck's full set. With
      // NOTHING on disk the answer depends on the meta: an explicit `cardCount`
      // of 0 is a genuinely empty deck (resident — no round trip to learn
      // nothing); an UNKNOWN count (legacy / recovered meta) must stay
      // non-resident, or the open flow would stop here on a deck the server
      // may well hold cards for — the permanent-empty failure
      // `deck-residency.ts` documents, reached from the disk side.
      const cardCount = s.deckProperties[deckId]?.cardCount;
      // `cardCount != null` first: the comment above is the contract, and
      // `>= (cardCount ?? 0)` did not implement it — an unknown count read as
      // "0 expected", so ANY row off disk marked the deck resident and loaded,
      // and the open flow stopped there on a deck the server still holds cards
      // for. Unknown stays non-resident; the server resolves it.
      const complete =
        merged !== undefined
          ? cardCount != null && bucketRowCount(merged) >= cardCount
          : cardCount === 0;
      if (!complete) return { cards, diskLoadedDeckIds };
      return { cards, diskLoadedDeckIds, ...residentFlags(s, deckId) };
    }),

  // ── Per-deck study summaries ──────────────────────────────────────────
  deckSummaries: {},

  applyDeckSummaries: (upsert, remove) =>
    set((s) => {
      const next = { ...s.deckSummaries, ...upsert };
      if (remove) for (const id of remove) delete next[id];
      return { deckSummaries: next };
    }),

  // ── Granular card mutations ──────────────────────────────────────────

  // Card mutators touch ONLY the cards slice — they no longer bump
  // `deckProperties[deckId].updatedAtISO`. Each card carries its own
  // per-entry timestamp, and the generic record engine pushes only
  // changed cards (no deck-level marker required). Bumping the deck
  // meta on every card edit emitted a wasteful `deckProperties-push`
  // (carrying the full meta blob — displayName, color, imageBackground
  // URL, etc.) for every star/edit/study action, and triggered the
  // linked-deck propagator on linked decks. Nothing user-facing reads
  // `deckProperties.updatedAtISO` (only the dev sync inspector).
  setCard: (deckId, card) => {
    noteLocalCardWrite(deckId);
    const cleaned = cleanCardForStore(card);
    // A card's contribution to the VISIBLE count changes in exactly two
    // cases: a brand-new id (add), or an existing card whose deleted-state
    // flips. A single soft-delete (`del`) and restore both route through
    // setCard on the SAME id (so `isNewCard` is false) but toggle
    // `isCardDeleted` — those must reconcile. A plain edit (study/star/flip)
    // leaves both the id-set and the deleted-state untouched, so it still
    // skips the reconcile entirely — keeping the hot path O(1) and push-free.
    const before = get();
    const allowed = reconcileAllowed(before, deckId);
    const prev = before.cards[deckId]?.[cleaned.id];
    const isNewCard = !prev;
    const deletedStateChanged =
      !isNewCard && isCardDeleted(prev) !== isCardDeleted(cleaned);
    mutate(
      set,
      (s) => ({
        cards: {
          ...s.cards,
          [deckId]: {
            ...s.cards[deckId],
            [cleaned.id]: cleaned,
          },
        },
      }),
      (st) => st.put('cards', { ...cleaned, deckId }),
    );
    if (isNewCard || deletedStateChanged) get().reconcileDeckCardCount(deckId, allowed);
  },

  setCardsBatch: (deckId, cards) => {
    if (cards.length === 0) return;
    noteLocalCardWrite(deckId);
    const allowed = reconcileAllowed(get(), deckId);
    const cleanedCards = cards.map(cleanCardForStore);
    set((s) => {
      const newDeckMap = { ...s.cards[deckId] };
      for (const card of cleanedCards) {
        newDeckMap[card.id] = card;
      }
      return { cards: { ...s.cards, [deckId]: newDeckMap } };
    });
    const storage = getSyncStorage();
    safeBulkPut(
      storage,
      'cards',
      cleanedCards.map((card) => ({ ...card, deckId })),
    );
    get().reconcileDeckCardCount(deckId, allowed);
  },

  removeCard: (deckId, cardId) => {
    noteLocalCardWrite(deckId);
    const allowed = reconcileAllowed(get(), deckId);
    mutate(
      set,
      (s) => {
        const deckCards = s.cards[deckId];
        if (!deckCards) return {};
        const { [cardId]: _, ...rest } = deckCards;
        return { cards: { ...s.cards, [deckId]: rest } };
      },
      (st) => st.removeByKey('cards', [deckId, cardId]),
    );
    get().reconcileDeckCardCount(deckId, allowed);
  },

  removeCards: (deckId, cardIds) => {
    if (cardIds.length === 0) return;
    noteLocalCardWrite(deckId);
    const allowed = reconcileAllowed(get(), deckId);
    const deckCards = get().cards[deckId];
    if (deckCards) {
      let next: Record<string, CardProps> | null = null;
      for (const id of cardIds) {
        if (!Object.prototype.hasOwnProperty.call(next ?? deckCards, id)) continue;
        next ??= { ...deckCards };
        delete next[id];
      }
      if (next) {
        const bucket = next;
        set((s) => ({ cards: { ...s.cards, [deckId]: bucket } }));
      }
    }
    safeBulkRemove(
      getSyncStorage(),
      'cards',
      cardIds.map((id) => [deckId, id]),
    );
    get().reconcileDeckCardCount(deckId, allowed);
  },

  reconcileDeckCardCount: (deckId, allowed = true) => {
    const s = get();
    // Never recompute an archived (cold) deck's count: its cards aren't
    // resident, so the live map is empty/partial and would clobber the real
    // server value the tile relies on.
    if (s.archivedDeckIds.has(deckId)) return;
    if (!allowed) return;
    const existing = s.deckProperties[deckId];
    if (!existing) return; // meta not created yet — first edit after this fixes it
    // Count only VISIBLE cards. Soft-deleted cards stay resident in the map
    // (scoped-deck `del`/`deleteMany` stamp `deletedTimeISO` + a `{delete}`
    // action without removing the entry), so a raw `Object.keys().length`
    // over-counts them. The cold/archived deck tile renders this persisted
    // `cardCount`, and a warm tile renders `getVisibleDeck(cards).length`
    // (which filters `isCardDeleted`) — counting visible cards here keeps the
    // two in step. Reuses the canonical `isCardDeleted` predicate so the
    // count can't drift from the read layer.
    const deckCards = s.cards[deckId];
    let count = 0;
    if (deckCards) {
      for (const id in deckCards) {
        if (!isCardDeleted(deckCards[id])) count++;
      }
    }
    if (existing.cardCount === count) return; // unchanged ⇒ no push (edits stay free)
    // Routes through the auto-stamping `setEntryDeckProperties` so detectDirty
    // fires the deckProperties push (cardCount is `personal` scope — no SG
    // fan-out). One push per genuine count change, coalesced by the sync loop.
    get().setEntryDeckProperties({ ...existing, cardCount: count });
  },

  // ── Sync boundary ───────────────────────────────────────────────────

  setDeckFromSync: (id, cards, updatedAtISO) => {
    noteLocalCardWrite(id);
    const ts = updatedAtISO ?? new Date().toISOString();
    const cleaned = cards.map(cleanCardForStore);
    const filled = backfillSortKeys(cleaned);
    const cardMap = cardsArrayToMap(filled);

    // A SERVER-sourced cards apply must not manufacture a deckProperties
    // change. The SG-mirror re-seed (`seedOrMergeSGMirror`) and the
    // linked-card fan-out (`applyAggregateLinkedCards`) re-apply unchanged
    // shared cards under `withServerSource` on every reconnect; bumping
    // `updatedAtISO` here would re-push the whole (unchanged) deck metadata
    // to UserDo on every refresh, because detectDirty keys off the timestamp
    // vs the persisted serverTs, not content. Only stamp for LOCAL
    // (user-initiated) writes — deck create / recovery / edit — where the
    // bump is what makes detectDirty fire. A server write for a deck with no
    // meta row yet still creates a minimal one. Mirrors the cards-only
    // invariant documented on `updateDeckCards` / `setCard` below.
    //
    // Residency: a LOCAL whole-deck write (create, import, fork, recovery,
    // undo) IS the deck's full set — loaded, disk-loaded, not archived. A
    // server-sourced one is flag-neutral: `linked-cards-updated` carries a
    // BATCH merged into whatever was resident, and the SG-mirror merge marks
    // residency itself once it knows the merged set is complete.
    // Persist only what changed when this device's rows for the deck already
    // match the previous bucket: the bucket existed and holds the deck's rows
    // (disk-loaded: memory ⊇ disk; loaded: memory ⊇ the deck, every row of
    // which reached storage through the path that put it in memory) AND the
    // storage keeps each deck's rows apart (`cardRowsKeyedByDeck` — on a store
    // keyed by card id alone a fork of this deck may have taken the rows over).
    // A card whose reference is unchanged is then byte-identical to its row.
    // Anything else — create, import, a new mirror, a partially-resident
    // bucket, an id-keyed store — keeps writing every row, which is also what
    // heals a disk that lags memory.
    const before = get();
    const prevBucket = before.cards[id];
    const storage = getSyncStorage();
    const writeOnlyChanged =
      prevBucket !== undefined &&
      storage.cardRowsKeyedByDeck === true &&
      (before.diskLoadedDeckIds.has(id) || before.loadedDeckIds.has(id));

    set((s) => {
      const existingMeta = s.deckProperties[id];
      const server = getMutationSource() === 'server';
      const skipMetaStamp = server && existingMeta !== undefined;
      return {
        ...(server ? null : residentFlags(s, id)),
        cards: { ...s.cards, [id]: cardMap },
        deckProperties: skipMetaStamp
          ? s.deckProperties
          : {
              ...s.deckProperties,
              // The minted minimal meta MUST carry the deck's identity:
              // an id-less row keys as "undefined" through entryKey and
              // as '' through the RN table router — invisible deck plus
              // crashed id-reading consumers. `deckPaths` is deliberately
              // NOT defaulted here: an SG mirror's real meta (with its
              // hiding `__sg_` path) may not have arrived yet, and a
              // fabricated path would flash the mirror into the library.
              [id]: {
                ...existingMeta,
                id: existingMeta?.id ?? (id as DeckProperties['id']),
                updatedAtISO: ts,
              },
            },
      };
    });

    const rows: Array<CardProps & { deckId: string }> = [];
    for (const card of filled) {
      if (writeOnlyChanged && prevBucket[card.id] === card) continue;
      rows.push({ ...card, deckId: id });
    }
    safeBulkPut(storage, 'cards', rows);
  },

  // ── Transform bridge ───────────────────────────────────────────────

  updateDeckCards: (id, transform) => {
    const cardMap = get().cards[id];
    if (!cardMap) return;
    noteLocalCardWrite(id);
    const allowed = reconcileAllowed(get(), id);

    const prevCards = cardsMapToArray(cardMap);
    const newCards = transform(prevCards).map(cleanCardForStore);
    const now = new Date().toISOString();
    const stamped = stampChangedCards(prevCards, newCards, now);
    const newMap = cardsArrayToMap(stamped);

    // Cards-only mutation — do NOT bump deckProperties.updatedAtISO
    // (see `setCard` comment for rationale).
    set((s) => ({ cards: { ...s.cards, [id]: newMap } }));

    // Where storage keeps each deck's rows apart (`cardRowsKeyedByDeck`),
    // persist only the cards whose reference changed: `cleanCardForStore` and
    // `stampChangedCards` return the SAME object for an untouched card, and
    // that object is already this deck's row. One grade on a 5k deck is one
    // row, not 5,000.
    //
    // Otherwise (web today: IndexedDB `cards` is keyed by card id alone) an
    // untouched card's row may belong to ANOTHER deck — a fork copies a deck
    // with the same card ids and takes the rows over — so every row of the
    // deck is rewritten, as before, which is what gives this deck its rows
    // back on its next edit. Still one batch (one transaction on web).
    const storage = getSyncStorage();
    const onlyChanged = storage.cardRowsKeyedByDeck === true;
    const rows: Array<CardProps & { deckId: string }> = [];
    for (const card of stamped) {
      if (!onlyChanged || cardMap[card.id] !== card) rows.push({ ...card, deckId: id });
    }
    safeBulkPut(storage, 'cards', rows);
    // Cards the transform dropped leave storage too (same key form as
    // `removeCard`, which deletes only this deck's row) — otherwise the next
    // disk load would bring them back.
    const dropped: Array<[string, string]> = [];
    for (const cardId in cardMap) {
      if (!Object.prototype.hasOwnProperty.call(newMap, cardId))
        dropped.push([id, cardId]);
    }
    safeBulkRemove(storage, 'cards', dropped);
    // A transform may add or remove cards (reorder/flip don't — those are a
    // cheap no-op inside the reconcile's count check).
    get().reconcileDeckCardCount(id, allowed);
  },

  setDeck: (id, cards, updatedAtISO) => {
    get().setDeckFromSync(id, cards, updatedAtISO);
  },

  // ── Deck properties & lifecycle ─────────────────────────────────────

  setDeckProperty: (id, props) => {
    // Routes through the framework's auto-stamping `setEntryDeckProperties`
    // (auto-generated by `createClientEntity` from the record-entity
    // schema). That setter unconditionally stamps `updatedAtISO: now()`
    // — without it detectDirty saw `entryTs === serverTs` and silently
    // dropped the push, so deck renames / recolors / folder moves
    // never reached UserDo. See the `rename deck via properties.set`
    // regression test in `sync-userdo-roundtrip.test.ts`.
    //
    // `id` is preserved on `props` (callers always include it); we
    // assert that here so a stale id arg doesn't silently get ignored.
    void id;
    get().setEntryDeckProperties(props);
  },

  removeDeck: (id) => {
    const deletedTimeISO = new Date().toISOString();

    // Land any coalesced server frames FIRST. A draft staged before the delete
    // publishes by cloning the CURRENT bucket, so a frame still pending would
    // re-create `cards[id]` under the deck the user just deleted — ghost cards,
    // re-pushed and re-summarised, and the summary row this delete removes
    // comes straight back. Same rule as `applyServerDeleteBroadcast`, which
    // drains for exactly this reason.
    drainPendingBulk();

    // Remove notes/annotations entries before the deck mutation. They are
    // record entities keyed by deckName; `removeEntryNotes` / `removeEntryAnnotations`
    // strip them from Zustand + IndexedDB, and the Zustand subscription emits
    // a `notes-delete` / `annotations-delete` push for us.
    // Look up deckPaths for the notes/annotations key
    const deckProps = get().deckProperties[id];
    const deckPath = deckProps?.deckPaths?.[0] ?? id;
    const state = get() as unknown as {
      notes?: Record<string, unknown>;
      annotations?: Record<string, unknown>;
      removeEntryNotes?: (key: string) => void;
      removeEntryAnnotations?: (key: string) => void;
    };
    if (state.notes && state.notes[deckPath]) {
      state.removeEntryNotes?.(deckPath);
    }
    if (state.annotations && state.annotations[deckPath]) {
      state.removeEntryAnnotations?.(deckPath);
    }

    // Note: the Yjs per-deck sub-map delete is now handled by the scoped
    // deck facade (`scoped-deck.remove()` calls `notesDeleteAll()` before
    // delegating here). Every user-initiated delete entry point — `d.remove()`,
    // `sync.decks.remove(id)`, `utils.deleteDeck()` — routes through that
    // facade, so we don't need to re-delete the sub-map here.
    //
    // The server-broadcast path (`applyServerDeckDelete`) is separate and
    // still cascades to Yjs explicitly — see `deleteDeckNotes` below.

    mutate(
      set,
      (s) => {
        const { [id]: _, ...restCards } = s.cards;
        const props = s.deckProperties[id];
        return {
          cards: restCards,
          deckSummaries: withoutSummary(s.deckSummaries, id),
          deckProperties: {
            ...s.deckProperties,
            // Tombstone + bump updatedAtISO so the generic engine's
            // record-entity detectDirty fires a push (its gate is
            // `entryTs <= serverTs → skip`; without the bump the
            // tombstone would carry the same ts as the previous
            // mirror and never flush). `id` is defaulted for the
            // props-less case (deck with no meta row yet) — an id-less
            // tombstone would fail the server's push validation and key
            // local persistence as ''.
            [id]: {
              ...props,
              id: props?.id ?? (id as DeckProperties['id']),
              deletedTimeISO,
              updatedAtISO: deletedTimeISO,
            },
          },
        };
      },
      (st) => {
        st.removeByIndex('cards', 'deckId', id);
        // The deck's summary goes with its rows (memory above, disk here) — a
        // re-created id must not inherit it at the next boot. Explicit rather
        // than left to the bridge's bucket diff: a cold deck has no bucket.
        st.removeByKey(DECK_STUDY_SUMMARY_TABLE, id);
        // deckSource is keyed (deckId, sourceType) — wipe all source_types for this deck.
        // deckPdf is keyed (deckId) — single-PK delete.
        st.removeByIndex('deckSource', 'deckId', id);
        st.removeByKey('deckPdf', id);
        st.updateByKey('deckProperties', id, {
          deletedTimeISO,
          updatedAtISO: deletedTimeISO,
        });
      },
    );
  },

  applyServerDeckDelete: (id) => {
    // Same resurrection guard as `removeDeck` above.
    drainPendingBulk();
    // Notes/annotations cascade from their own server-side `*-deleted`
    // broadcasts — no local cleanup needed here.
    //
    // The per-user Yjs notes doc's own sub-map delete is replicated via the
    // Yjs protocol from the originating device — by the time a server
    // broadcast reaches us, Yjs has (or will) carry the sub-map delete too.
    // Still call `deleteDeckNotes` here for idempotent belt-and-suspenders:
    // if the Yjs message hasn't arrived yet (rare race), this ensures a
    // consistent view on this device. Uses the shared helper so the same
    // path-resolution logic as `d.remove()` applies.
    deleteDeckNotes(id);

    mutate(
      set,
      (s) => {
        const { [id]: _, ...restCards } = s.cards;
        return { cards: restCards, deckSummaries: withoutSummary(s.deckSummaries, id) };
      },
      (st) => {
        st.removeByIndex('cards', 'deckId', id);
        st.removeByKey(DECK_STUDY_SUMMARY_TABLE, id);
      },
    );
  },

  moveDeckToFolder: (id, folderId) => {
    const existing = get().deckProperties[id];
    if (!existing) return;
    get().setEntryDeckProperties({ ...existing, folder: folderId });
  },

  reorderDeck: (id, sortKey) => {
    const existing = get().deckProperties[id];
    if (!existing) return;
    get().setEntryDeckProperties({ ...existing, sortKey });
  },

  batchUpdateDeckProperties: (updates) => {
    if (updates.length === 0) return;
    for (const { id, partial } of updates) {
      const existing = get().deckProperties[id];
      if (!existing) continue;
      get().setEntryDeckProperties({ ...existing, ...partial });
    }
  },
});
